import { ProjectSuggestions } from '@platform/client';
import { Modal, ModalCallToAction, Spinner } from '@platform/components';
import { useState } from 'react';
import { GreenCircleCheckmark } from '../../../svg/GreenCircleCheckmark';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'wouter';

export const CreateProjectModal = ({
  projectSuggestionId,
  organizationId,
  onClose,
  projectSuggestionIndex,
}: {
  projectSuggestionId: number;
  organizationId: number;
  onClose: () => void;
  projectSuggestionIndex: number;
}) => {
  const [modalStage, setModalStage] = useState<'confirm' | 'creating' | 'success' | 'error'>(
    'confirm',
  );
  const [newProjectId, setNewProjectId] = useState<number>();
  const queryClient = useQueryClient();
  const createProjectMutate = ProjectSuggestions.useCreateProjectFromSuggestion({
    mutation: {
      onSuccess: (response) => {
        queryClient.invalidateQueries({
          queryKey: [...ProjectSuggestions.getGetProjectSuggestionsQueryKey(organizationId)],
        });
        if (response.projectId) {
          setNewProjectId(response.projectId);
          setModalStage('success');
        }
      },
      onError: () => {
        setModalStage('error');
      },
    },
  });
  const primaryCta: ModalCallToAction = {
    text: 'Yes, Create Project',
    callback: () => {
      setModalStage('creating');
      createProjectMutate.mutate({ projectSuggestionId });
    },
    isDisabled: () => modalStage === 'creating',
  };
  if (modalStage === 'error' || modalStage === 'success') {
    primaryCta.text = 'Close';
    primaryCta.callback = onClose;
  }
  const secondaryCta: ModalCallToAction | undefined =
    modalStage === 'confirm' ?
      {
        text: 'No, Cancel',
        callback: onClose,
      }
    : undefined;

  return (
    <Modal
      headerDivision='divider'
      isOpen={true}
      onClose={onClose}
      title={`Create project from suggestion #${projectSuggestionIndex + 1}?`}
      variant='dialog'
      primaryCTA={primaryCta}
      secondaryCTA={secondaryCta}
      ignoreExtraPadding
    >
      {modalStage === 'confirm' && (
        <p>
          A project will be created in 'review' stage. You can view and edit the project after
          creation.
        </p>
      )}
      {modalStage === 'creating' && (
        <div className='flex justify-center items-center'>
          <Spinner />
        </div>
      )}
      {modalStage === 'error' && (
        <p className='text-red-500'>There was an error creating this project. Please try again.</p>
      )}
      {modalStage === 'success' && (
        <div className='flex flex-col items-center gap-2'>
          <GreenCircleCheckmark />
          <p>Project successfully created!</p>
          {newProjectId && (
            <>
              <Link
                className='blue-link text-sm'
                to={`~/platform/admin/projects/${newProjectId}/edit/`}
              >
                Edit Project
              </Link>
              <Link className='blue-link text-sm' to={`~/platform/admin/projects/${newProjectId}/`}>
                View Project
              </Link>
              <Link
                className='blue-link text-sm'
                to={`~/platform/directory/projects/${newProjectId}/`}
              >
                View Project Preview
              </Link>
            </>
          )}
        </div>
      )}
    </Modal>
  );
};
